import { useEffect, useMemo, useRef, useState } from 'react';
import { useSurface } from '@/contexts/SurfaceContext';
import { useKioskOfflineScreen } from '@/hooks/useKioskOfflineScreen';
import { getCachedProvidersSync } from '@/features/games/services/gameProviderService';
import { getIconPathForProvider } from '@/features/games/data/gameProviders';
import { paintGoldCoin } from '@/features/vfx/data/goldCoinSprite';
import './KioskOfflineOverlay.css';

const DEFAULT_ICON = '/icons/egame.jpg';

type OfflineTile = { key: string; name: string; icon: string };
type ReelState   = { index: number; durationMs: number };

// Strip length: SAFE_REPS*len + maxExtraLoops*len + (len-1) = 2*4 + 16*4 + 3 = 75 → use 22 repeats
const REEL_REPEAT_COUNT = 22;
const SAFE_REPS         = 2;

/** Read tile height from a rendered reel DOM node; fallback to 140. */
function getTileHeight(reelEl: Element | null): number {
  if (!reelEl) return 140;
  const tile = reelEl.querySelector<HTMLElement>('.kiosk-offline-tile');
  return tile ? tile.offsetHeight || 140 : 140;
}

const FALLBACK_TILES: { name: string; code: string }[] = [
  { name: 'VegasX',      code: 'vegasx'     },
  { name: 'Fire Kirin',  code: 'firekirin'  },
  { name: 'Game Vault',  code: 'gamevault'  },
  { name: 'Juwa 777',    code: 'juwa'       },
  { name: 'RiverPay',    code: 'riverpay'   },
  { name: 'VBlink777',   code: 'vblink777'  },
  { name: 'Ultra Panda', code: 'ultrapanda' },
  { name: 'Cash Frenzy', code: 'cashfrenzy' },
  { name: 'Blue Dragon', code: 'bluedragon' },
];

function tilesFromProviders(): OfflineTile[] {
  const cached = getCachedProvidersSync();
  const items  = cached?.data?.items;
  if (items?.length) {
    return items.map((p) => ({ key: p.id, name: p.name, icon: getIconPathForProvider(p.code || '') }));
  }
  return FALLBACK_TILES.map((t, i) => ({ key: `fb-${i}`, name: t.name, icon: getIconPathForProvider(t.code) }));
}

function buildColumns(tiles: OfflineTile[]): [OfflineTile[], OfflineTile[], OfflineTile[]] {
  let list = [...tiles];
  let padRound = 0;
  while (list.length < 9) {
    FALLBACK_TILES.forEach((t, i) => {
      if (list.length >= 12) return;
      list.push({ key: `pad-${padRound}-${i}`, name: t.name, icon: getIconPathForProvider(t.code) });
    });
    padRound += 1;
  }
  list = list.slice(0, 12);
  const c: [OfflineTile[], OfflineTile[], OfflineTile[]] = [[], [], []];
  list.forEach((tile, i) => { c[i % 3].push(tile); });
  return c;
}

/**
 * Fullscreen "On connecting" screensaver when network is down, on kiosk and mobile surfaces (the
 * `offlineScreen` capability, src/config/surfacePolicy.ts; not while the surface is unknown). Renders nothing elsewhere (`?offlinePreview=1` shows it).
 */
export default function KioskOfflineOverlay() {
  const { can }     = useSurface();
  const { show }    = useKioskOfflineScreen(can('offlineScreen'));
  const forcePreview =
    typeof window !== 'undefined' &&
    new URLSearchParams(window.location.search).get('offlinePreview') === '1';

  const columns = useMemo(() => {
    const tiles = tilesFromProviders();
    return buildColumns(tiles);
  }, [show, forcePreview]);

  const [reelStates, setReelStates] = useState<ReelState[]>([]);
  const [tileHeight, setTileHeight] = useState(140);
  const [winFlash,   setWinFlash]   = useState(false);
  const [coinBurst,  setCoinBurst]  = useState(0);
  const shellRef    = useRef<HTMLElement | null>(null);
  const coinCanvasRef = useRef<HTMLCanvasElement | null>(null);

  // ── Gold coin explosion — single unified loop ──────────────────────
  const coinSpriteRef = useRef<HTMLCanvasElement | null>(null);
  const coinLoopRef   = useRef(0);

  const MAX_COINS = 24;
  type Coin = {
    x: number; y: number; vx: number; vy: number;
    r: number; rot: number; rotV: number;
    alpha: number; grounded: boolean; age: number;
  };
  const coinsRef = useRef<Coin[]>([]);

  // Build the coin sprite once (96px — smaller = faster drawImage on low-end)
  useEffect(() => {
    const SS = 96;
    const c = document.createElement('canvas');
    c.width = c.height = SS;
    paintGoldCoin(c.getContext('2d')!, SS);
    coinSpriteRef.current = c;
  }, []);

  // Single animation loop — runs whenever there are coins to draw
  const startCoinLoop = () => {
    if (coinLoopRef.current) return; // already running
    const canvas = coinCanvasRef.current;
    const sprite = coinSpriteRef.current;
    if (!canvas || !sprite) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const coins = coinsRef.current;
    const GRAVITY  = 0.072;
    const FLOOR_Y  = canvas.height - 36;
    const FADE_RATE = 1 / 300; // ~5 seconds at 60 fps

    const tick = () => {
      if (coins.length === 0) {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        coinLoopRef.current = 0;
        return;
      }

      ctx.clearRect(0, 0, canvas.width, canvas.height);

      for (let i = coins.length - 1; i >= 0; i--) {
        const c = coins[i];

        if (c.grounded) {
          // On the floor: fade out steadily
          c.age++;
          c.alpha -= FADE_RATE;
          if (c.alpha <= 0) { coins.splice(i, 1); continue; }

          ctx.save();
          ctx.globalAlpha = c.alpha;
          ctx.translate(c.x, c.y);
          ctx.scale(1, 0.32);
          ctx.rotate(c.rot);
          ctx.drawImage(sprite, -c.r, -c.r, c.r * 2, c.r * 2);
          ctx.restore();
        } else {
          // Flying: physics
          c.vy  += GRAVITY;
          c.x   += c.vx;
          c.y   += c.vy;
          c.rot += c.rotV;

          if (c.y >= FLOOR_Y) {
            if (c.vy > 0.8) {
              // Bounce
              c.y   = FLOOR_Y;
              c.vy  = -Math.abs(c.vy) * 0.28;
              c.vx *= 0.6;
              c.rotV *= 0.4;
            } else {
              // Settle on the ground
              c.grounded = true;
              c.y   = FLOOR_Y + Math.random() * 4;
              c.vx  = 0;
              c.vy  = 0;
              c.rot = (Math.random() - 0.5) * 0.25;
              c.rotV = 0;
              c.alpha = 0.85;
              c.age = 0;
              continue;
            }
          }

          ctx.save();
          ctx.globalAlpha = 1;
          ctx.translate(c.x, c.y);
          ctx.rotate(c.rot);
          const squish = Math.abs(Math.cos(c.rot * 1.4)) * 0.35 + 0.65;
          ctx.scale(squish, 1);
          ctx.drawImage(sprite, -c.r, -c.r, c.r * 2, c.r * 2);
          ctx.restore();
        }
      }

      coinLoopRef.current = requestAnimationFrame(tick);
    };

    coinLoopRef.current = requestAnimationFrame(tick);
  };

  // Spawn flying coins on each qualifying burst
  useEffect(() => {
    if (coinBurst === 0) return;
    if (coinBurst % 3 !== 0) return;

    const canvas = coinCanvasRef.current;
    if (!canvas) return;
    canvas.width  = window.innerWidth;
    canvas.height = window.innerHeight;

    const originX = canvas.width  * 0.5;
    const originY = canvas.height * 0.52;
    const pool    = coinsRef.current;

    // 20 → 16 coins per burst (20% fewer)
    const spawnCount = 16;
    while (pool.length + spawnCount > MAX_COINS && pool.length > 0) pool.shift();

    for (let n = 0; n < spawnCount; n++) {
      const angle = Math.random() * Math.PI * 2;
      const spd   = 0.8 + Math.random() * 2.2;
      pool.push({
        x: originX + (Math.random() - 0.5) * 120,
        y: originY + (Math.random() - 0.5) * 50,
        vx: Math.cos(angle) * spd,
        vy: Math.sin(angle) * spd - (2.8 + Math.random() * 3.2),
        r: 28 + Math.random() * 14,
        rot: Math.random() * Math.PI * 2,
        rotV: (Math.random() - 0.5) * 0.04,
        alpha: 1,
        grounded: false,
        age: 0,
      });
    }

    startCoinLoop();
  }, [coinBurst]);

  // Clean up the coin loop only on full unmount
  useEffect(() => {
    return () => {
      if (coinLoopRef.current) { cancelAnimationFrame(coinLoopRef.current); coinLoopRef.current = 0; }
    };
  }, []);

  // ── Measure real tile height after mount/resize ───────────────────────
  useEffect(() => {
    const measure = () => {
      const h = getTileHeight(shellRef.current?.querySelector('.kiosk-offline-reel') ?? null);
      setTileHeight(h);
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);

  // ── Initialise reels at safe start position ───────────────────────────
  useEffect(() => {
    setReelStates(
      columns.map((col) => {
        const len = Math.max(col.length, 1);
        return { index: SAFE_REPS * len, durationMs: 0 };
      }),
    );
  }, [columns]);

  // ── Spin cycle ────────────────────────────────────────────────────────
  useEffect(() => {
    if (columns.every((col) => col.length === 0)) return;

    let cancelled = false;
    const timers: number[] = [];
    const rafs:   number[] = [];

    /**
     * Spin forward from startIndexes.
     * Formula: focused tile = (index+1)%len  →  target%len = (landOn-1+len)%len
     */
    const runSpinCycle = (startIndexes: number[]) => {
      if (cancelled) return;

      const targets = columns.map((col, ci) => {
        const len       = Math.max(col.length, 1);
        const extraLoops = 12 + ci * 2; // col0=12, col1=14, col2=16 → sequential stop
        const landOn    = Math.floor(Math.random() * len);
        const targetMod = (landOn - 1 + len) % len;
        const startMod  = startIndexes[ci] % len;
        const delta     = (targetMod - startMod + len) % len;
        return startIndexes[ci] + extraLoops * len + delta;
      });

      setReelStates(
        targets.map((idx, ci) => ({
          index: idx,
          durationMs: 2100 + ci * 560, // col0=2100ms, col1=2660ms, col2=3220ms (×1.4)
        })),
      );

      const maxDuration = 2100 + (columns.length - 1) * 560; // 3220ms (×1.4)

      // Trigger win effects ~120ms after last reel settles
      timers.push(
        window.setTimeout(() => {
          if (cancelled) return;
          setWinFlash(true);
          setCoinBurst((n) => n + 1);
          timers.push(
            window.setTimeout(() => { if (!cancelled) setWinFlash(false); }, 1700),
          );
        }, maxDuration + 120),
      );

      // After stop + 2 s pause: snap to safe range then spin again
      timers.push(
        window.setTimeout(() => {
          if (cancelled) return;
          const safeIndexes = targets.map((tgt, ci) => {
            const len = Math.max(columns[ci]?.length ?? 1, 1);
            return SAFE_REPS * len + (tgt % len);
          });
          setReelStates(safeIndexes.map((idx) => ({ index: idx, durationMs: 0 })));
          const raf1 = requestAnimationFrame(() => {
            const raf2 = requestAnimationFrame(() => { if (!cancelled) runSpinCycle(safeIndexes); });
            rafs.push(raf2);
          });
          rafs.push(raf1);
        }, maxDuration + 2000),
      );
    };

    timers.push(
      window.setTimeout(() => {
        const initIndexes = columns.map((col) => {
          const len = Math.max(col.length, 1);
          return SAFE_REPS * len;
        });
        runSpinCycle(initIndexes);
      }, 400),
    );

    return () => {
      cancelled = true;
      timers.forEach((id) => window.clearTimeout(id));
      rafs.forEach((id)   => cancelAnimationFrame(id));
    };
  }, [columns]);

  if (!show && !forcePreview) return null;

  return (
    <div className="kiosk-offline-root" role="alert" aria-live="polite" aria-busy="true">


      {/* 3D floor at bottom */}
      <div className="kiosk-offline-floor" aria-hidden />

      {/* Coin explosion canvas */}
      <canvas ref={coinCanvasRef} className="kiosk-offline-coins" aria-hidden />

      {/* Top-anchored brand + status labels */}
      <div className="kiosk-offline-header">
        <h1 className="kiosk-offline-brand">Kiosk Gaming</h1>
        <div className="kiosk-offline-heading">
          <h2 className="kiosk-offline-title">On connecting</h2>
          <div className="kiosk-offline-dots" aria-hidden>
            <span /><span /><span />
          </div>
        </div>
        <p className="kiosk-offline-sub">Please wait while network connection is being restored</p>
      </div>

      <section className="kiosk-offline-shell" ref={shellRef}>
        <div className="kiosk-offline-reels">
          {columns.map((col, ci) => (
            <div key={ci} className={`kiosk-offline-reel${winFlash ? ' kiosk-offline-reel--winning' : ''}`}>
              <div className="kiosk-offline-focus" />
              <div
                className={`kiosk-offline-strip s${ci + 1}`}
                style={{
                  transform:        `translate3d(0, -${(reelStates[ci]?.index ?? 0) * tileHeight}px, 0)`,
                  transitionDuration: `${reelStates[ci]?.durationMs ?? 0}ms`,
                }}
              >
                {Array.from({ length: REEL_REPEAT_COUNT }).flatMap((_, repeatIndex) =>
                  col.map((tile, tileIndex) => (
                    <div key={`${tile.key}-strip-${repeatIndex}-${tileIndex}`} className="kiosk-offline-tile">
                      <span className="kiosk-offline-icon-wrap">
                        <img
                          className="kiosk-offline-icon"
                          src={tile.icon}
                          alt=""
                          loading="lazy"
                          decoding="async"
                          onError={(e) => { e.currentTarget.src = DEFAULT_ICON; }}
                        />
                      </span>
                      <span className="kiosk-offline-name">{tile.name}</span>
                    </div>
                  )),
                )}
              </div>
            </div>
          ))}
        </div>
        <div className="kiosk-offline-footer">Auto reconnecting network ...</div>
      </section>
    </div>
  );
}
